import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { StaffFormDialog } from "@/components/StaffFormDialog";
import { getStaffById, deleteStaff, fullName, initials } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Phone, Mail, MapPin, Cake, IdCard, Users, Building2, Globe, Facebook, Instagram, Twitter, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const toMinutes = (t) => {
  if (!t) return null;
  const m = t.trim().match(/^(\d{1,2}):(\d{2})\s*(am|pm)?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const ap = m[3] ? m[3].toLowerCase() : null;
  if (ap === "pm" && h !== 12) h += 12;
  else if (ap === "am" && h === 12) h = 0;
  else if (!ap) h = h >= 8 ? h : h + 12; // fallback for legacy 24-ish values
  return h * 60 + min;
};

const currentPeriodIndex = (schedule) => {
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const starts = schedule.map((p) => toMinutes(p.time));
  for (let i = 0; i < schedule.length; i++) {
    const start = starts[i];
    const end = i + 1 < schedule.length ? starts[i + 1] : (start != null ? start + 50 : null);
    if (start != null && end != null && nowMin >= start && nowMin < end) return i;
  }
  return -1;
};

export default function StaffDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [staff, setStaff] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = async () => {
    try {
      setStaff(await getStaffById(id));
    } catch {
      setNotFound(true);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const onDelete = async () => {
    await deleteStaff(id);
    toast.success("Staff member deleted");
    navigate("/");
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F3F5F8]">
        <Header />
        <div className="p-16 text-center text-[#0A1F44]/60" data-testid="detail-not-found">
          Staff member not found.
        </div>
      </div>
    );
  }
  if (!staff) {
    return (
      <div className="min-h-screen bg-[#F3F5F8]">
        <Header />
        <div className="p-16 text-[#0A1F44]/50">Loading…</div>
      </div>
    );
  }

  const curIdx = currentPeriodIndex(staff.schedule || []);

  return (
    <div className="min-h-screen bg-[#F3F5F8]">
      <Header>
        {isAdmin && (
          <>
            <Button
              data-testid="edit-staff-btn"
              onClick={() => setEditOpen(true)}
              variant="outline"
              className="rounded-none border-[#C0982E] text-[#C0982E] hover:bg-[#C0982E] hover:text-[#0A1F44] gap-2 text-xs uppercase tracking-wider transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" /> Edit
            </Button>
            <Button
              data-testid="delete-staff-btn"
              onClick={() => setDelOpen(true)}
              className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E] hover:text-[#0A1F44] gap-2 text-xs uppercase tracking-wider transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </Button>
          </>
        )}
      </Header>

      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-8">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-[#0A1F44]/60 hover:text-[#C0982E] transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to directory
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="aspect-[3/4] w-1/2 lg:w-1/2 max-w-[200px] bg-[#0A1F44]/5 overflow-hidden border border-[#0A1F44]/20 flex items-center justify-center">
              {staff.avatar_url ? (
                <img src={staff.avatar_url} alt={fullName(staff)} className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700" />
              ) : (
                <span className="font-serif text-4xl text-[#0A1F44]/40">{initials(staff)}</span>
              )}
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="text-xs uppercase tracking-[0.25em] text-[#C0982E] mb-2" data-testid="detail-department">
              {staff.department || "Faculty"}{staff.department2 && staff.department2.trim().toLowerCase() !== (staff.department || "").trim().toLowerCase() ? ` / ${staff.department2}` : ""}
            </div>
            <h1 data-testid="detail-name" className="font-serif text-5xl sm:text-6xl text-[#0A1F44] leading-none">
              {fullName(staff)}
            </h1>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 border-l border-t border-[#0A1F44]/20">
              <InfoCell icon={Phone} label="Phone" value={staff.phone} testid="detail-phone" href={staff.phone ? `tel:${staff.phone}` : null} />
              <InfoCell icon={Mail} label="Email" value={staff.email} testid="detail-email" href={staff.email ? `mailto:${staff.email}` : null} />
              <InfoCell icon={Building2} label="School Campus" value={staff.school_campus} testid="detail-campus" />
              <InfoCell icon={MapPin} label="Room" value={staff.room ? `Rm: ${staff.room}` : ""} testid="detail-room" />
              <InfoCell icon={Cake} label="Birthday" value={staff.birthday} testid="detail-birthday" />
              <InfoCell icon={IdCard} label="Staff ID" value={staff.staff_id} testid="detail-staffid" />
              <InfoCell icon={Users} label="Admin Supervisor" value={staff.department_head} testid="detail-depthead" />
            </div>
          </div>
        </div>

        {(staff.website || staff.facebook || staff.instagram || staff.x_handle) && (
          <div className="mt-14" data-testid="connect-section">
            <h2 className="font-serif text-3xl text-[#0A1F44] mb-5">Connect</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 border-l border-t border-[#0A1F44]/20">
              <SocialCell icon={Globe} label="Website" value={staff.website} kind="website" testid="detail-website" />
              <SocialCell icon={Facebook} label="Facebook" value={staff.facebook} kind="facebook" testid="detail-facebook" />
              <SocialCell icon={Instagram} label="Instagram" value={staff.instagram} kind="instagram" testid="detail-instagram" />
              <SocialCell icon={Twitter} label="X Handle" value={staff.x_handle} kind="x" testid="detail-x" />
            </div>
          </div>
        )}

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-[#0A1F44] mb-1">Daily Schedule</h2>
          <p className="text-sm text-[#0A1F44]/50 mb-5">
            {curIdx >= 0 ? "Current period highlighted in terracotta." : "No class in session right now."}
          </p>
          <table data-testid="schedule-table" className="w-full border-collapse border border-[#0A1F44]/20 text-sm">
            <thead>
              <tr className="bg-[#E6EAF1]">
                <th className="border border-[#0A1F44]/20 p-4 text-left text-xs uppercase tracking-wider">Period</th>
                <th className="border border-[#0A1F44]/20 p-4 text-left text-xs uppercase tracking-wider">Time</th>
                <th className="border border-[#0A1F44]/20 p-4 text-left text-xs uppercase tracking-wider">Class</th>
              </tr>
            </thead>
            <tbody>
              {(staff.schedule || []).map((p, i) => (
                <tr
                  key={i}
                  data-testid={`schedule-row-${i}`}
                  className={i === curIdx ? "bg-[#C0982E] text-[#F3F5F8]" : ""}
                >
                  <td className="border border-[#0A1F44]/20 p-4 font-medium">{p.period}</td>
                  <td className="border border-[#0A1F44]/20 p-4">{p.time}</td>
                  <td className="border border-[#0A1F44]/20 p-4 font-serif text-base">{p.subject}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <StaffFormDialog open={editOpen} onOpenChange={setEditOpen} existing={staff} onSaved={load} />

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent className="rounded-none border border-[#0A1F44] bg-[#F3F5F8]" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete {fullName(staff)}?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this staff member from the directory.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-[#0A1F44]" data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} className="rounded-none bg-[#b3402a] hover:bg-[#8f3221] text-white" data-testid="delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const InfoCell = ({ icon: Icon, label, value, testid, href }) => (
  <div className="border-r border-b border-[#0A1F44]/20 p-5">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 mb-1">
      <Icon className="w-3 h-3" strokeWidth={1.5} /> {label}
    </div>
    {href && value ? (
      <a href={href} data-testid={testid} className="text-[#0A1F44] hover:text-[#C0982E] transition-colors break-all">
        {value}
      </a>
    ) : (
      <div data-testid={testid} className="text-[#0A1F44] break-all">{value || "—"}</div>
    )}
  </div>
);

const socialHref = (kind, value) => {
  const v = (value || "").trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@/, "").trim();
  if (/\s/.test(v) || /^(n\/?a|none|na)$/i.test(v)) return null; // page names / N/A → not a link
  if (kind === "instagram") return `https://instagram.com/${handle}`;
  if (kind === "x") return `https://x.com/${handle}`;
  if (kind === "website") return `https://${v}`;
  return null;
};

const SocialCell = ({ icon: Icon, label, value, kind, testid }) => {
  const href = socialHref(kind, value);
  return (
    <div className="border-r border-b border-[#0A1F44]/20 p-5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 mb-1">
        <Icon className="w-3 h-3" strokeWidth={1.5} /> {label}
      </div>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className="text-[#0A1F44] hover:text-[#C0982E] transition-colors break-all">
          {value}
        </a>
      ) : (
        <div data-testid={testid} className="text-[#0A1F44] break-all">{value || "—"}</div>
      )}
    </div>
  );
};
